import { assign, StateNodeConfig } from "xstate";

import isPersonalEmail from "@brexhq/next/src/utils/isPersonalEmail";
import { EmployeeCountV3, LeadFormStatus } from "@brexhq/next/types/brex-api";

import {
  isEcomm,
  areMonthlySalesUnder400K,
  employeeLessThan50,
  employeeBetween51and250,
  segUnidentifiedSendToPasswordScreen,
  employeeGreaterThan250,
} from "./helpers";
import {
  LeadFormSteps,
  leadFormEvents,
  leadFormMachineContext,
  leadFormMachineEvent,
  LeadFormStepSchema,
} from "./types";

type StateNode = StateNodeConfig<
  leadFormMachineContext,
  LeadFormStepSchema,
  leadFormMachineEvent
>;

/**
 * OBS4 sub machines
 */

export const segSBSubMachine: StateNode = {
  id: "segSBSubMachine",
  initial: LeadFormSteps.ecommerce,
  states: {
    [LeadFormSteps.ecommerce]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.monthlySales,
            cond: (_, event) => isEcomm(event.value?.isEcomm),
            actions: ["updateFields"],
          },
          {
            target: LeadFormSteps.fundingSources,
            actions: ["updateFields"],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: `#${LeadFormSteps.website}`,
            cond: (ctx) => isPersonalEmail(ctx.workEmail || ""),
          },
          {
            target: `#${LeadFormSteps.userInfo}`,
          },
        ],
      },
    },
    [LeadFormSteps.monthlySales]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.fundingSources,
            cond: (_, event) => {
              return areMonthlySalesUnder400K(event.value?.monthlySales);
            },
            actions: [
              "updateFields",
              assign({
                hasSeenMonthlySales: () => true,
              }),
            ],
          },
          {
            target: LeadFormSteps.updateLead,
            actions: [
              "updateFields",
              assign({
                hasSeenMonthlySales: () => true,
                status: () => LeadFormStatus.COMPLETED,
              }),
            ],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: LeadFormSteps.ecommerce,
            actions: [
              assign({
                hasSeenMonthlySales: () => false,
              }),
            ],
          },
        ],
      },
    },
    [LeadFormSteps.fundingSources]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.updateLead,
            actions: [
              "updateFields",
              assign({
                status: () => LeadFormStatus.COMPLETED,
                hasSeenFundingSource: () => true,
              }),
            ],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: LeadFormSteps.monthlySales,
            cond: (ctx) => {
              return isEcomm(ctx.isEcomm);
            },
            actions: [
              assign({
                hasSeenFundingSource: () => false,
              }),
            ],
          },
          {
            target: LeadFormSteps.ecommerce,
            actions: [
              assign({
                hasSeenFundingSource: () => false,
              }),
            ],
          },
        ],
      },
    },
    [LeadFormSteps.updateLead]: {
      invoke: {
        src: "updateLeadMutation",
        onDone: [
          {
            target: `#${LeadFormSteps.password}`,
            actions: assign({
              salesSegment: (_, event) => event.data.salesSegment,
            }),
          },
        ],
      },
    },
  },
};

export const segUnidentifiedSubMachine: StateNode = {
  id: "segUnidentifiedSubMachine",
  initial: LeadFormSteps.employeeCount,
  states: {
    [LeadFormSteps.employeeCount]: {
      on: {
        [leadFormEvents.submit]: [
          {
            cond: (_, event) => employeeLessThan50(event.value?.employeeCount),
            target: LeadFormSteps.ecommerce,
            actions: ["updateFields"],
          },
          {
            cond: (_, event) =>
              employeeBetween51and250(event.value?.employeeCount),
            target: LeadFormSteps.fundingSources,
            actions: ["updateFields"],
          },
          {
            target: LeadFormSteps.updateLead,
            actions: [
              "updateFields",
              assign({
                status: () => LeadFormStatus.COMPLETED,
              }),
            ],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: `#${LeadFormSteps.website}`,
            cond: (ctx) => isPersonalEmail(ctx.workEmail || ""),
          },
          {
            target: `#${LeadFormSteps.userInfo}`,
          },
        ],
      },
    },
    [LeadFormSteps.ecommerce]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.monthlySales,
            cond: (_, event) => {
              return isEcomm(event.value?.isEcomm);
            },
            actions: ["updateFields"],
          },
          {
            target: LeadFormSteps.fundingSources,
            actions: ["updateFields"],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: LeadFormSteps.employeeCount,
          },
        ],
      },
    },
    [LeadFormSteps.monthlySales]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.fundingSources,
            cond: (_, event) => {
              return areMonthlySalesUnder400K(event.value?.monthlySales);
            },
            actions: ["updateFields"],
          },
          {
            target: LeadFormSteps.updateLead,
            actions: [
              "updateFields",
              assign({
                status: () => LeadFormStatus.COMPLETED,
              }),
            ],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: LeadFormSteps.ecommerce,
            actions: [
              assign({
                hasSeenMonthlySales: () => false,
              }),
            ],
          },
        ],
      },
    },
    [LeadFormSteps.fundingSources]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.updateLead,
            actions: [
              "updateFields",
              assign({
                status: () => LeadFormStatus.COMPLETED,
              }),
            ],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: LeadFormSteps.monthlySales,
            cond: (ctx) => {
              return isEcomm(ctx.isEcomm);
            },
          },
          {
            target: LeadFormSteps.ecommerce,
            cond: (ctx) => {
              return employeeLessThan50(ctx.employeeCount as EmployeeCountV3);
            },
          },
          {
            target: LeadFormSteps.employeeCount,
          },
        ],
      },
    },
    [LeadFormSteps.updateLead]: {
      invoke: {
        src: "updateLeadMutation",
        onDone: [
          {
            target: `#leadFormV2.${LeadFormSteps.password}`,
            cond: (ctx, event) =>
              segUnidentifiedSendToPasswordScreen(ctx, event.data),
            actions: assign({
              salesSegment: (_, event) => event.data.salesSegment,
            }),
          },
          {
            target: `#leadFormV2.${LeadFormSteps.success}`,
          },
        ],
        onError: [
          {
            target: LeadFormSteps.employeeCount,
            cond: (ctx) =>
              employeeGreaterThan250(ctx.employeeCount as EmployeeCountV3),
            actions: assign({
              errorMessage: (_, event) => {
                return event.data;
              },
            }),
          },
          {
            target: LeadFormSteps.monthlySales,
            cond: (ctx) => !areMonthlySalesUnder400K(ctx.monthlySales),
            actions: assign({
              errorMessage: (_, event) => {
                return event.data;
              },
            }),
          },
          {
            target: LeadFormSteps.fundingSources,
            actions: assign({
              errorMessage: (_, event) => {
                return event.data;
              },
            }),
          },
        ],
      },
    },
  },
};

export const segESTierTwoSubMachine: StateNode = {
  id: "segESTierTwoSubMachine",
  initial: LeadFormSteps.ecommerce,
  states: {
    [LeadFormSteps.ecommerce]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.monthlySales,
            cond: (_, event) => isEcomm(event.value?.isEcomm),
            actions: ["updateFields"],
          },
          {
            target: LeadFormSteps.updateLead,
            actions: [
              "updateFields",
              assign({
                status: () => LeadFormStatus.COMPLETED,
              }),
            ],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: `#${LeadFormSteps.website}`,
            cond: (ctx) => isPersonalEmail(ctx.workEmail || ""),
          },
          {
            target: `#${LeadFormSteps.userInfo}`,
          },
        ],
      },
    },
    [LeadFormSteps.monthlySales]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.updateLead,
            actions: [
              "updateFields",
              assign({
                status: () => LeadFormStatus.COMPLETED,
              }),
            ],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: LeadFormSteps.ecommerce,
          },
        ],
      },
    },
    [LeadFormSteps.updateLead]: {
      invoke: {
        src: "updateLeadMutation",
        onDone: [
          {
            target: `#leadFormV2.${LeadFormSteps.password}`,
            actions: assign({
              salesSegment: (_, event) => event.data.salesSegment,
            }),
          },
        ],
        onError: [
          {
            target: LeadFormSteps.monthlySales,
            cond: (ctx) => isEcomm(ctx.isEcomm),
            actions: assign({
              errorMessage: (_, event) => {
                return event.data;
              },
            }),
          },
          {
            target: LeadFormSteps.ecommerce,
            actions: assign({
              errorMessage: (_, event) => {
                return event.data;
              },
            }),
          },
        ],
      },
    },
  },
};

export const segGrowthTier2SubMachine: StateNode = {
  id: "segGrowthTier2SubMachine",
  initial: LeadFormSteps.fundingSources,
  states: {
    [LeadFormSteps.fundingSources]: {
      on: {
        [leadFormEvents.submit]: [
          {
            target: LeadFormSteps.updateLead,
            actions: [
              "updateFields",
              assign({
                status: () => LeadFormStatus.COMPLETED,
              }),
            ],
          },
        ],
        [leadFormEvents.back]: [
          {
            target: `#${LeadFormSteps.userInfo}`,
          },
        ],
      },
    },
    [LeadFormSteps.updateLead]: {
      invoke: {
        src: "updateLeadMutation",
        onDone: [
          {
            target: `#${LeadFormSteps.password}`,
          },
        ],
      },
    },
  },
};
